import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { QueryClient, useMutation } from 'react-query';
import { useWindowSize } from 'react-use';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { AlertVariant, MOBILE_BREAKPOINT } from '../../../../constants/constants';
import { VENUE_PERMISSION_GROUP_LIST } from '../../../../constants/queryKeys';
import { _Permission, _PermissionGroup, _ToastMessageProps, _VenuePickerOption } from '../../../../constants/staticTypes';
import { MANAGE_PERMISSIONS, SELECT_VENUE } from '../../../../constants/strings';
import { AuthCtx } from '../../../../context/AuthCtxProvider';
import { defaultToastData } from '../../../../helpers/commonHelper';
import HttpServiceHelper from '../../../../helpers/httpServiceHelper';
import { JTranslation } from '../../../../helpers/jTranslationHelper';
import {
    _RolePermissions,
    _UpdatePermissionForm,
    convertToRolePermissions,
    updateOrgFullPermissions,
} from '../../../../helpers/permissionHelper';
import withSidebar from '../../../../hoc/withSideBar/withSideBar';
import { useQueryHook } from '../../../../hooks/useQueryHook';
import { useTranslation } from '../../../../hooks/useTranslation';
import logger from '../../../../utils/logger';
import AddVenuePermission from './components/AddVenuePermission';
import VenuePermissionSidebar from './components/VenuePermissionSidebar';
import VenuePermissionDetails from './components/VenuePermissionDetails';
import VenuePermissionFooter from './components/VenuePermissionFooter';
import MlxDropDown from '../../../../components/common/MlxDropDown/MlxDropDown';
import {
    deleteVenuePermission,
    getVenuePermissionWithGroupList,
    onVenuePermissionChange,
    updateVenuePermission,
} from '../../../../helpers/venuePermissionHelper';
import { getVenueOptions } from '../../../../helpers/venueHelper';
import MlxBlockUI from '../../../../components/common/MlxBlockUI/MlxBlockUI';

const languageTexts = {
    MANAGE_PERMISSIONS: MANAGE_PERMISSIONS,
    SELECT_VENUE: SELECT_VENUE,
};

function VenuePermission() {
    const queryClient = new QueryClient();
    const deletePermissionMutation = useMutation(HttpServiceHelper);
    const updatePermissionMutation = useMutation(HttpServiceHelper);
    const { width: screenWidth } = useWindowSize();
    const translatedText = useTranslation(languageTexts, 'pascal');
    const { userType } = useContext(AuthCtx);

    const [venuePermissionList, setVenuePermissionList] = useState<_Permission[]>([] as _Permission[]);
    const [venueGroupPermissionList, setVenueGroupPermissionList] = useState<_PermissionGroup[]>([] as _PermissionGroup[]);
    const [activeGroupPermission, setActiveGroupPermission] = useState<_PermissionGroup | null>(null);
    const [activeGroupPermissionList, setActiveGroupPermissionList] = useState<_Permission[] | null>();
    const [updatePermissionForm, setUpdatePermissionForm] = useState<_UpdatePermissionForm | null>(null);
    const [addAdminOrgPermissionModel, setAddAdminOrgPermissionModel] = useState<boolean>(false);
    const [resetKey, setResetKey] = useState(0);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [venueOptions, setVenueOptions] = useState<_VenuePickerOption[]>([]);
    const [selectedVenueOption, setSelectedVenueOption] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isUpdatingPermission, setIsUpdatingPermission] = useState<boolean>(false);

    useEffect(() => {
        setIsLoading(true);
        const getVenues = async () => {
            const venueOptionsList = await getVenueOptions(queryClient);
            setVenueOptions(venueOptionsList);
            venueOptionsList?.length > 0 && setSelectedVenueOption(venueOptionsList[0].id);
        };
        getVenues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // get admin permission and admin group permission list
    const venuePermissionGroupListQuery = useQueryHook({
        queryKey: VENUE_PERMISSION_GROUP_LIST,
        queryFn: async () => await getVenuePermissionWithGroupList(queryClient, selectedVenueOption),
        onSuccess: (response: any) => {
            const { venuePermissionListData, venuePermissionGroupListData } = response;

            setVenueGroupPermissionList(venuePermissionGroupListData);
            setVenuePermissionList(venuePermissionListData);

            // set first permission as default, if no permission selected
            if (venuePermissionGroupListData && venuePermissionGroupListData.length > 0) {
                setActiveGroupPermission(venuePermissionGroupListData[0]);
            }
            setIsLoading(false);
        },
        onError: (error) => logger.log('Failed to fetch permission list', error),
        refetchOnWindowFocus: false,
        enabled: false,
        enableLoader: false,
    });

    useEffect(() => {
        if (!selectedVenueOption) return;
        setIsLoading(true);
        venuePermissionGroupListQuery.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVenueOption]);

    // on permission change
    const onPermissionChangeEvent = (event: ChangeEvent<HTMLInputElement>, permission: _Permission) => {
        const updatedPermissions = onVenuePermissionChange(permission, activeGroupPermissionList as _Permission[], event.target.checked);
        setActiveGroupPermissionList(updatedPermissions ?? []);
    };

    // Delete permission
    const onDeletePermission = (permissionId: string) => {
        setIsLoading(true);
        deleteVenuePermission(
            deletePermissionMutation,
            { id: permissionId },
            (data: any) => {
                if (activeGroupPermission?.id === permissionId) {
                    setActiveGroupPermission(null);
                }
                setVenueGroupPermissionList((prev) => prev.filter((permissionGroup) => permissionGroup.id !== permissionId));
                setToastMessage({
                    message: data?.message ?? '',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                venuePermissionGroupListQuery.refetch();
            },
            (error: string, variant: string) => {
                setIsLoading(false);
                setToastMessage({
                    message: error,
                    variant,
                    show: true,
                });
            },
            selectedVenueOption
        );
    };

    // on update permission
    const onUpdatePermission = () => {
        if (!updatePermissionForm) return;

        setIsLoading(true);
        setIsUpdatingPermission(true);
        updateVenuePermission(
            updatePermissionMutation,
            updatePermissionForm,
            (data: any) => {
                setIsUpdatingPermission(false);
                setToastMessage({
                    message: data?.message ?? '',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                venuePermissionGroupListQuery.refetch();
            },
            (error: string, variant: string) => {
                setIsLoading(false);
                setIsUpdatingPermission(false);
                setToastMessage({
                    message: error,
                    variant,
                    show: true,
                });
            },
            selectedVenueOption
        );
    };

    useEffect(() => {
        if (!venuePermissionList || !activeGroupPermission) return;

        const currentActivePermissionGroup = venueGroupPermissionList.find((permission) => permission.id === activeGroupPermission.id);
        if (currentActivePermissionGroup) {
            const updatedPermissionList = updateOrgFullPermissions(venuePermissionList, currentActivePermissionGroup?.permissions ?? []);
            setActiveGroupPermissionList(updatedPermissionList);
        }
    }, [venueGroupPermissionList, venuePermissionList, activeGroupPermission, resetKey]);

    useEffect(() => {
        if (activeGroupPermissionList && activeGroupPermission) {
            const rolePermissions: _RolePermissions[] = convertToRolePermissions(activeGroupPermissionList);
            const updatedPermissionForm: _UpdatePermissionForm = {
                id: activeGroupPermission.id,
                permissionRoleName: activeGroupPermission.permissionRoleName ?? '',
                permissionRoleDescription: activeGroupPermission.permissionRoleDescription ?? '',
                rolePermissions,
            };
            setUpdatePermissionForm(updatedPermissionForm);
        }
    }, [activeGroupPermissionList, activeGroupPermission]);

    return (
        <MlxBlockUI isLoading={isLoading} opacity={0.3}>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <ConfirmPopup className="mlx-confirm-popup" />
            <AddVenuePermission
                setToastMessage={setToastMessage}
                visible={addAdminOrgPermissionModel}
                setVisible={setAddAdminOrgPermissionModel}
                setActiveGroupPermission={setActiveGroupPermission}
                setIsLoading={setIsLoading}
                selectedTenantId={selectedVenueOption}
            />

            <div className="page-section">
                <span className="page-header d-flex justify-content-between">
                    <span>
                        <JTranslation typeCase="capitalize" text={translatedText.MANAGE_PERMISSIONS} />
                    </span>
                    <MlxDropDown
                        dropDownSize="large"
                        options={venueOptions}
                        optionLabel="venueName"
                        optionValue="id"
                        value={selectedVenueOption}
                        placeholder={translatedText.SELECT_VENUE}
                        onChange={(e) => {
                            setSelectedVenueOption(e.value);
                        }}
                        style={{ width: '200px' }}
                    />
                </span>
                <div className="page-content h-100">
                    <div className="content-body">
                        <div className="row g-3 h-100 m-0">
                            <div className={`col-md-5 col-lg-4 ${screenWidth < MOBILE_BREAKPOINT ? 'mb-3' : 'h-100'} pe-0`}>
                                <VenuePermissionSidebar
                                    venueGroupPermissionList={venueGroupPermissionList}
                                    activeGroupPermission={activeGroupPermission}
                                    setActiveGroupPermission={setActiveGroupPermission}
                                    userType={userType}
                                    queryClient={queryClient}
                                    onAddClick={() => setAddAdminOrgPermissionModel(true)}
                                    onDeletePermission={onDeletePermission}
                                />
                            </div>

                            <div className={`col-md-7 col-lg-8 ${screenWidth < MOBILE_BREAKPOINT ? '' : 'h-100'} ps-0`}>
                                <div className="org-permission-body mb-5 d-flex flex-column position-relative">
                                    <div className="d-flex flex-column overflow-auto p-1">
                                        {activeGroupPermission && (
                                            <VenuePermissionDetails
                                                activeGroupPermission={activeGroupPermission}
                                                setActiveGroupPermission={setActiveGroupPermission}
                                                activeGroupPermissionList={activeGroupPermissionList}
                                                onPermissionChangeEvent={onPermissionChangeEvent}
                                            />
                                        )}
                                    </div>

                                    <VenuePermissionFooter
                                        userType={userType}
                                        queryClient={queryClient}
                                        venueGroupPermissionList={venueGroupPermissionList}
                                        activeGroupPermission={activeGroupPermission}
                                        setActiveGroupPermission={setActiveGroupPermission}
                                        setResetKey={setResetKey}
                                        onUpdatePermission={onUpdatePermission}
                                        isSaving={isUpdatingPermission}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MlxBlockUI>
    );
}

export default withSidebar(VenuePermission);
